import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  generateResearch,
  getIntentTemplates,
  createCampaignFromPlan,
  type MarketingPlan,
  type IntentTemplate,
} from '../../api/planner';

const FALLBACK_INTENTS: IntentTemplate[] = [
  { id: 'lead_gen', label: 'Lead Generation', icon: '🎯', description: 'Cold outreach to acquire new customers and generate qualified leads', exampleIdeas: 'Target small business owners who recently launched a website.' },
  { id: 're_engage', label: 'Re-engage Customers', icon: '🔄', description: 'Win back churned or inactive customers with a compelling offer', exampleIdeas: 'Reach out to customers inactive for 90+ days.' },
  { id: 'upsell', label: 'Upsell / Expand', icon: '⬆️', description: 'Grow revenue from existing customers by offering upgrades or add-ons', exampleIdeas: 'Contact customers on basic plan about premium features.' },
  { id: 'announce', label: 'Announce / Launch', icon: '📣', description: 'Announce a new product, feature, or company update to your audience', exampleIdeas: 'We just launched AI analytics. Call users to demo.' },
  { id: 'survey', label: 'Survey / Feedback', icon: '📋', description: 'Collect customer feedback, NPS scores, or market research data', exampleIdeas: 'Post-purchase satisfaction survey.' },
  { id: 'custom', label: 'Custom', icon: '✏️', description: 'Define your own campaign intent and strategy from scratch', exampleIdeas: 'Describe your unique campaign goal.' },
];

const PROGRESS_STEPS = [
  'Researching competitors...',
  'Analyzing target audience...',
  'Crafting messaging strategy...',
  'Writing call script...',
  'Building campaign plan...',
  'Finalizing insights...',
];

type Tab = 'audience' | 'messaging' | 'script' | 'campaign' | 'outcomes';

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: 'audience', label: 'Audience', icon: '🧠' },
  { id: 'messaging', label: 'Messaging', icon: '💬' },
  { id: 'script', label: 'Script', icon: '📞' },
  { id: 'campaign', label: 'Campaign', icon: '📅' },
  { id: 'outcomes', label: 'Outcomes', icon: '📊' },
];

export const MarketingPlanner: React.FC = () => {
  const navigate = useNavigate();
  const [intents, setIntents] = useState<IntentTemplate[]>(FALLBACK_INTENTS);

  // Form state
  const [product, setProduct] = useState('');
  const [intent, setIntent] = useState('');
  const [ideas, setIdeas] = useState('');
  const [industry, setIndustry] = useState('');

  // Result state
  const [plan, setPlan] = useState<MarketingPlan | null>(null);
  const [planId, setPlanId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progressIdx, setProgressIdx] = useState(0);
  const [activeTab, setActiveTab] = useState<Tab>('audience');
  const [launchLoading, setLaunchLoading] = useState(false);

  useEffect(() => {
    getIntentTemplates().then(setIntents).catch(() => {});
  }, []);

  // Animate progress steps during loading
  useEffect(() => {
    if (!loading) return;
    setProgressIdx(0);
    const interval = setInterval(() => {
      setProgressIdx((prev) => (prev < PROGRESS_STEPS.length - 1 ? prev + 1 : prev));
    }, 3500);
    return () => clearInterval(interval);
  }, [loading]);

  const handleGenerate = async () => {
    if (!product.trim() || !intent || !ideas.trim()) return;
    setLoading(true);
    setError(null);
    setPlan(null);

    try {
      const result = await generateResearch({
        product: product.trim(),
        intent,
        ideas: ideas.trim(),
        industry: industry.trim() || undefined,
      });
      setPlan(result.plan);
      setPlanId(result.planId);
      setActiveTab('audience');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to generate marketing plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = () => {
    handleGenerate();
  };

  const handleLaunchCampaign = async () => {
    if (!planId) return;
    setLaunchLoading(true);
    try {
      await createCampaignFromPlan(planId);
      navigate('/campaigns');
    } catch {
      setError('Failed to create campaign. Please try again.');
    } finally {
      setLaunchLoading(false);
    }
  };

  const handleBack = () => {
    setPlan(null);
    setPlanId(null);
    setError(null);
  };

  const selectedIntent = intents.find((i) => i.id === intent);

  // ── FORM VIEW ────────────────────────────────────────────
  if (!plan) {
    return (
      <div className="px-4 md:px-8 py-8 max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">AI Marketing Planner</h1>
          <p style={{ color: '#9ca3af' }} className="text-sm">
            Define your intent and ideas — AI generates a complete marketing research plan with call scripts.
          </p>
        </div>

        {/* Product */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-white mb-1.5">Product / Service</label>
          <input
            type="text"
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            placeholder="e.g. CallPilot AI — automated phone outreach platform"
            className="w-full px-3 py-2.5 rounded-lg text-sm text-white placeholder-gray-500 outline-none"
            style={{
              background: '#111118',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          />
        </div>

        {/* Intent selector */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-white mb-2">Campaign Intent</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
            {intents.map((t) => {
              const isSelected = intent === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setIntent(t.id)}
                  className="text-left rounded-xl p-3.5 transition-all duration-150"
                  style={{
                    background: isSelected ? 'rgba(99,102,241,0.15)' : '#111118',
                    border: isSelected
                      ? '1.5px solid rgba(99,102,241,0.5)'
                      : '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <div className="text-xl mb-1.5">{t.icon}</div>
                  <div className="text-sm font-medium text-white">{t.label}</div>
                  <div className="text-xs mt-0.5" style={{ color: '#9ca3af' }}>
                    {t.description}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Ideas */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-white mb-1.5">
            Ideas & Context
          </label>
          {selectedIntent && (
            <p className="text-xs mb-1.5" style={{ color: '#6b7280' }}>
              e.g. {selectedIntent.exampleIdeas}
            </p>
          )}
          <textarea
            value={ideas}
            onChange={(e) => setIdeas(e.target.value)}
            placeholder="What angles do you want to try? Any key messages, offers, or notes..."
            rows={4}
            className="w-full px-3 py-2.5 rounded-lg text-sm text-white placeholder-gray-500 outline-none resize-y"
            style={{
              background: '#111118',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          />
        </div>

        {/* Industry */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-white mb-1.5">
            Industry <span style={{ color: '#6b7280' }}>(optional)</span>
          </label>
          <input
            type="text"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            placeholder="e.g. SaaS, Real Estate, Healthcare"
            className="w-full px-3 py-2.5 rounded-lg text-sm text-white placeholder-gray-500 outline-none"
            style={{
              background: '#111118',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          />
        </div>

        {error && (
          <div
            className="mb-4 px-4 py-3 rounded-lg text-sm"
            style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.2)', color: '#f87171' }}
          >
            {error}
          </div>
        )}

        {/* Generate button */}
        <button
          onClick={handleGenerate}
          disabled={loading || !product.trim() || !intent || !ideas.trim()}
          className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all duration-150"
          style={{
            background: loading || !product.trim() || !intent || !ideas.trim()
              ? 'rgba(99,102,241,0.3)'
              : 'linear-gradient(135deg, #6366f1, #818cf8)',
            cursor: loading || !product.trim() || !intent || !ideas.trim() ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? 'Generating...' : 'Generate Marketing Plan'}
        </button>

        {/* Progress animation */}
        {loading && (
          <div className="mt-6">
            {PROGRESS_STEPS.map((step, i) => (
              <div
                key={step}
                className="flex items-center gap-2.5 py-1.5 transition-all duration-300"
                style={{
                  opacity: i <= progressIdx ? 1 : 0.3,
                  transform: i <= progressIdx ? 'translateX(0)' : 'translateX(8px)',
                }}
              >
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{
                    background: i < progressIdx ? '#34d399' : i === progressIdx ? '#6366f1' : '#374151',
                  }}
                />
                <span
                  className="text-sm"
                  style={{
                    color: i < progressIdx ? '#34d399' : i === progressIdx ? '#ffffff' : '#6b7280',
                  }}
                >
                  {step}
                </span>
                {i === progressIdx && (
                  <span className="animate-pulse text-xs" style={{ color: '#6366f1' }}>
                    ...
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── RESULT VIEW ──────────────────────────────────────────
  return (
    <div className="px-4 md:px-8 py-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <button
            onClick={handleBack}
            className="text-sm mb-2 flex items-center gap-1"
            style={{ color: '#9ca3af' }}
          >
            ← Back to form
          </button>
          <h1 className="text-xl font-bold text-white">{product}</h1>
          <p className="text-sm" style={{ color: '#9ca3af' }}>
            {selectedIntent?.icon} {selectedIntent?.label} plan
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRegenerate}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={{ background: 'rgba(255,255,255,0.06)', color: '#d1d5db', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            {loading ? 'Regenerating...' : 'Regenerate'}
          </button>
          <button
            onClick={handleLaunchCampaign}
            disabled={launchLoading}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
            style={{ background: '#6366f1' }}
          >
            {launchLoading ? 'Creating...' : 'Launch Campaign'}
          </button>
        </div>
      </div>

      {error && (
        <div
          className="mb-4 px-4 py-3 rounded-lg text-sm"
          style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.2)', color: '#f87171' }}
        >
          {error}
        </div>
      )}

      {/* Tabs */}
      <div
        className="flex gap-1 mb-6 overflow-x-auto pb-1"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-all rounded-t-lg"
              style={{
                color: isActive ? '#ffffff' : '#9ca3af',
                background: isActive ? 'rgba(99,102,241,0.1)' : 'transparent',
                borderBottom: isActive ? '2px solid #6366f1' : '2px solid transparent',
              }}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div
        className="rounded-xl p-5 md:p-6"
        style={{ background: '#111118', border: '1px solid rgba(255,255,255,0.06)' }}
      >
        {activeTab === 'audience' && <AudienceTab plan={plan} />}
        {activeTab === 'messaging' && <MessagingTab plan={plan} />}
        {activeTab === 'script' && <ScriptTab plan={plan} />}
        {activeTab === 'campaign' && <CampaignTab plan={plan} />}
        {activeTab === 'outcomes' && <OutcomesTab plan={plan} />}
      </div>
    </div>
  );
};

// ── TAB COMPONENTS ───────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#6366f1' }}>{children}</div>;
}

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={`rounded-lg p-4 ${className}`}
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}
    >
      {children}
    </div>
  );
}

function AudienceTab({ plan }: { plan: MarketingPlan }) {
  const { audienceProfile: a } = plan;
  return (
    <div className="space-y-5">
      <div>
        <SectionLabel>Primary Persona</SectionLabel>
        <p className="text-sm text-white leading-relaxed">{a.primaryPersona}</p>
      </div>
      <div>
        <SectionLabel>Pain Points</SectionLabel>
        <div className="flex flex-wrap gap-2">
          {a.painPoints.map((p, i) => (
            <span
              key={i}
              className="px-3 py-1.5 rounded-full text-xs font-medium"
              style={{ background: 'rgba(248,113,113,0.1)', color: '#f87171', border: '1px solid rgba(248,113,113,0.15)' }}
            >
              {p}
            </span>
          ))}
        </div>
      </div>
      <Card>
        <SectionLabel>Psychographics</SectionLabel>
        <p className="text-sm leading-relaxed" style={{ color: '#d1d5db' }}>{a.psychographics}</p>
      </Card>
      <Card>
        <SectionLabel>Best Time to Call</SectionLabel>
        <p className="text-sm leading-relaxed" style={{ color: '#d1d5db' }}>{a.bestTimeToCall}</p>
      </Card>
    </div>
  );
}

function MessagingTab({ plan }: { plan: MarketingPlan }) {
  const { messagingStrategy: m } = plan;
  return (
    <div className="space-y-5">
      <Card>
        <SectionLabel>Core Message</SectionLabel>
        <p className="text-base font-medium text-white">{m.coreMessage}</p>
      </Card>
      <Card>
        <SectionLabel>Hook (first 5 seconds)</SectionLabel>
        <p className="text-sm text-white italic">"{m.hook}"</p>
      </Card>
      <div>
        <SectionLabel>Value Propositions</SectionLabel>
        <div className="space-y-2">
          {m.valueProps.map((vp, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-sm mt-0.5" style={{ color: '#34d399' }}>✓</span>
              <span className="text-sm" style={{ color: '#d1d5db' }}>{vp}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <SectionLabel>Objection Handling</SectionLabel>
        <div className="space-y-3">
          {Object.entries(m.objectionHandling).map(([objection, response]) => (
            <Card key={objection}>
              <p className="text-sm font-medium mb-1" style={{ color: '#fbbf24' }}>"{objection}"</p>
              <p className="text-sm" style={{ color: '#d1d5db' }}>{response}</p>
            </Card>
          ))}
        </div>
      </div>
      <Card>
        <SectionLabel>Tone & Style</SectionLabel>
        <p className="text-sm" style={{ color: '#d1d5db' }}>{m.toneAndStyle}</p>
      </Card>
    </div>
  );
}

function ScriptTab({ plan }: { plan: MarketingPlan }) {
  const { callScript: s } = plan;
  const [showFull, setShowFull] = useState(false);

  return (
    <div className="space-y-5">
      {!showFull ? (
        <>
          <Card>
            <SectionLabel>Opening</SectionLabel>
            <p className="text-sm leading-relaxed" style={{ color: '#d1d5db' }}>{s.opening}</p>
          </Card>
          <Card>
            <SectionLabel>Body</SectionLabel>
            <p className="text-sm leading-relaxed" style={{ color: '#d1d5db' }}>{s.body}</p>
          </Card>
          <Card>
            <SectionLabel>Call to Action</SectionLabel>
            <p className="text-sm leading-relaxed font-medium text-white">{s.cta}</p>
          </Card>
          <div>
            <SectionLabel>Objection Responses</SectionLabel>
            <div className="space-y-3">
              {Object.entries(s.objectionResponses).map(([obj, resp]) => (
                <Card key={obj}>
                  <p className="text-sm font-medium mb-1" style={{ color: '#fbbf24' }}>"{obj}"</p>
                  <p className="text-sm" style={{ color: '#d1d5db' }}>{resp}</p>
                </Card>
              ))}
            </div>
          </div>
          <Card>
            <SectionLabel>Closing</SectionLabel>
            <p className="text-sm leading-relaxed" style={{ color: '#d1d5db' }}>{s.closing}</p>
          </Card>
        </>
      ) : (
        <Card>
          <SectionLabel>Full Script</SectionLabel>
          <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: '#d1d5db' }}>{s.fullScript}</p>
        </Card>
      )}
      <button
        onClick={() => setShowFull(!showFull)}
        className="text-sm font-medium transition-all"
        style={{ color: '#818cf8' }}
      >
        {showFull ? 'Show sections' : 'View full script'}
      </button>
    </div>
  );
}

function CampaignTab({ plan }: { plan: MarketingPlan }) {
  const { campaignStructure: c, competitorIntel: ci } = plan;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card>
          <SectionLabel>Recommended Contacts</SectionLabel>
          <p className="text-sm" style={{ color: '#d1d5db' }}>{c.recommendedContacts}</p>
        </Card>
        <Card>
          <SectionLabel>Call Timing</SectionLabel>
          <p className="text-sm" style={{ color: '#d1d5db' }}>{c.callTiming}</p>
        </Card>
      </div>
      <Card>
        <SectionLabel>Follow-Up Cadence</SectionLabel>
        <p className="text-sm" style={{ color: '#d1d5db' }}>{c.followUpCadence}</p>
      </Card>
      <div>
        <SectionLabel>Sequence Steps</SectionLabel>
        <div className="space-y-2">
          {c.sequenceSteps.map((step, i) => (
            <div
              key={i}
              className="flex items-start gap-3 px-3 py-2.5 rounded-lg"
              style={{ background: 'rgba(255,255,255,0.03)' }}
            >
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5"
                style={{ background: 'rgba(99,102,241,0.2)', color: '#818cf8' }}
              >
                {i + 1}
              </div>
              <p className="text-sm" style={{ color: '#d1d5db' }}>{step}</p>
            </div>
          ))}
        </div>
      </div>
      {ci && (
        <div className="pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <SectionLabel>Competitor Intel</SectionLabel>
          <div className="space-y-3 mt-2">
            <div className="flex flex-wrap gap-2">
              {ci.topCompetitors.map((c, i) => (
                <span
                  key={i}
                  className="px-3 py-1.5 rounded-full text-xs font-medium"
                  style={{ background: 'rgba(99,102,241,0.1)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.15)' }}
                >
                  {c}
                </span>
              ))}
            </div>
            <Card>
              <p className="text-sm mb-2" style={{ color: '#9ca3af' }}>
                <strong style={{ color: '#d1d5db' }}>Their messaging:</strong> {ci.theirMessaging}
              </p>
              <p className="text-sm" style={{ color: '#9ca3af' }}>
                <strong style={{ color: '#34d399' }}>Your differentiator:</strong> {ci.differentiator}
              </p>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

function OutcomesTab({ plan }: { plan: MarketingPlan }) {
  const { expectedOutcomes: e } = plan;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card>
          <SectionLabel>Estimated Answer Rate</SectionLabel>
          <p className="text-2xl font-bold text-white mb-1">{e.estimatedAnswerRate}</p>
        </Card>
        <Card>
          <SectionLabel>Estimated Conversion Rate</SectionLabel>
          <p className="text-2xl font-bold text-white mb-1">{e.estimatedConversionRate}</p>
        </Card>
      </div>
      <Card>
        <div className="flex items-start gap-2">
          <span className="text-lg mt-0.5">💡</span>
          <div>
            <SectionLabel>Tip to Improve</SectionLabel>
            <p className="text-sm leading-relaxed" style={{ color: '#d1d5db' }}>{e.tipToImprove}</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
